import { ContentType, CountryCode, HeaderKeys } from '@qred/shared-enums';
import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { CardOfferSigner, IFileInformation } from '~/Interfaces';
import { V2 } from '~/constants/constVars';
import { DocumentType } from '~/constants/fileUpload';
import { ClientAssessmentQuestionnaireTypeId } from '~/enums';
import { logToSentry } from '~/helpers/loggers.helper';
import { buildPostOfferFormData } from '~/helpers/shared.helper';
import { getCookieValue, getMarketBasedOnHostname } from '~/helpers/utils';
import { ILoanOfferForm } from '~/interfaces/loanOffer';
import { IKYCQuestionnaireAnswer } from '~/interfaces/shared';
import {
  CardApplicationDTO,
  cardApplicationDTOSchema,
  CardApplicationPostPatchResponseDTO,
  cardApplicationPostPatchResponseSchema,
} from '~/schemas/DTO/cardApplicationSchemas';
import { cardOfferDTOSchemas } from '~/schemas/DTO/cardOffer';
import { loanApplicationDTOSchemas } from '~/schemas/DTO/loanApplication';
import { loanOfferDTOSchemas } from '~/schemas/DTO/loanOffer';
import {
  getCompanySearchResDTOSchema,
  sharedDTOSchemas,
} from '~/schemas/DTO/shared';
import { accessToken } from '~/store/apis/utils/accessToken';
import { TCardOfferDTO } from '~/types/DTO/cardOffer';
import { TLoanApplicationDTO } from '~/types/DTO/loanApplication';
import { TLoanOfferDTO } from '~/types/DTO/loanOffer';
import { TSharedDTO } from '~/types/DTO/shared';
import { TCompanySearchResults } from '~/types/types';

axios.defaults.baseURL = import.meta.env.VITE_API_MYPAGES_URL;

declare module 'axios' {
  export interface AxiosRequestConfig {
    retry?: {
      retryCount: number;
      retryDelay: number;
      retryUntil: (data: any) => boolean;
    };
  }

  export interface AxiosResponse {
    maxRetriesReached?: boolean;
  }
}

export const axiosWithRetry = axios.create();

if (
  import.meta.env.MODE === 'development' &&
  import.meta.env.VITE_WAF_BYPASS_HEADER_VALUE
) {
  const addWafBypassHeader = (config: InternalAxiosRequestConfig) => {
    config.headers[
      'X-Api-Acquisition-Waf'
    ] = import.meta.env.VITE_WAF_BYPASS_HEADER_VALUE;

    return config;
  };

  axios.interceptors.request.use(addWafBypassHeader);
  axiosWithRetry.interceptors.request.use(addWafBypassHeader);
}

axiosWithRetry.interceptors.response.use(async (response) => {
  const { config, data } = response;

  const maxRetriesReached = config.retry?.retryCount === 0;
  const responseFulfilled = config.retry?.retryUntil(data);

  if (!config.retry || maxRetriesReached || responseFulfilled) {
    return Promise.resolve({
      ...response,
      maxRetriesReached: maxRetriesReached && !responseFulfilled,
    });
  }

  config.retry.retryCount -= 1;
  const delayRetryRequest = new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, config.retry?.retryDelay);
  });
  await delayRetryRequest;
  return axiosWithRetry(config);
}, undefined);

// =================================== HELPERS ===================================
export const getApiVersionPathParam = (apiVersion: string | undefined) =>
  apiVersion === V2 ? `${V2}/` : '';

/** *******************************************************************************
 *
 * GET requests should be prefixed with 'apiGet' and POST requests with 'apiPost'
 *
 ******************************************************************************** */

export const apiGetEskatIntegrationUrl = (qcode: string) =>
  axios.get(`${import.meta.env.VITE_API_ESKAT_INTEGRATION_BASE_URL}/${qcode}`);

// =================================== POST ===================================

export const apiPostSignerConfirmation = () => {
  const ACCESS_TOKEN =
    getCookieValue('additionalSignerAccessToken') ||
    getCookieValue('publicToken');
  const signerId =
    getCookieValue('additionalSignerId') || getCookieValue('iSig');
  const market = getMarketBasedOnHostname();

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  return axios.post('/applications/signer', null, {
    headers: {
      [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
    },
    params: {
      market,
      signerId,
      userAgreementConsent: true,
      coSigningConsent: true,
    },
  });
};

export const apiPostSignerCardOfferData = (
  data: CardOfferSigner,
  market: string
) => {
  const ACCESS_TOKEN =
    getCookieValue('additionalSignerAccessToken') ||
    getCookieValue('publicToken');

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const requestData = {
    hasAcceptedTerms: true,
  };

  return axios.post(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${market}/card-offer/${data.signerUuid}`,
    requestData,
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
    }
  );
};

export const apiPostInitiateOTP = async (
  market: string,
  signerId: string | null,
  hashCode: string | null,
  productType: string | null
) => {
  const response = await axios.post<TSharedDTO['postInitiateOTPRes']>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${market.toLowerCase()}/generate-additional-signer-otp?signerId=${signerId}&hashCode=${hashCode}&regenerateOtp=${false}&product=${productType}`
  );

  const parseResponseResult = sharedDTOSchemas.postInitiateOTPRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiPostInitiateOTP parseResponseResult'
    );
  }
  return response;
};

export const apiPostRegenerateOTP = async (
  market: string,
  signerId: string | null,
  hashCode: string | null,
  productType: string | null
) => {
  const response = await axios.post<TSharedDTO['postRegenerateOTPRes']>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${market.toLowerCase()}/generate-additional-signer-otp?signerId=${signerId}&hashCode=${hashCode}&regenerateOtp=${true}&product=${productType}`
  );

  const parseResponseResult = sharedDTOSchemas.postRegenerateOTPRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiPostRegenerateOTP parseResponseResult'
    );
  }
  return response;
};

export const apiPostVerifyOTP = async (
  market: string,
  otp: string,
  signerId: string | null,
  hashCode: string | null,
  productType: string | null
) => {
  const response = await axios.post<TSharedDTO['postVerifyOTPRes']>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${market}/verify-additional-signer-otp/${otp}?signerId=${signerId}&hashCode=${hashCode}&product=${productType}`
  );

  const parseResponseResult = sharedDTOSchemas.postVerifyOTPRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiPostVerifyOTP parseResponseResult'
    );
  }
  return response;
};

export const apiGetAdditionalSignerLoanApplication = async (
  market: string | null,
  signerId: string | null
) => {
  const ACCESS_TOKEN = getCookieValue('additionalSignerAccessToken');

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const response = await axios.get<
    TSharedDTO['getAdditionalSignerLoanApplicationDataRes']
  >(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${market}/get-additional-signer-application?product=loan&signerId=${signerId}`,
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
    }
  );

  const parseResponseResult = sharedDTOSchemas.getAdditionalSignerLoanApplicationDataRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiGetAdditionalSignerLoanApplication parseResponseResult'
    );
  }
  return response;
};

export const apiGetAdditionalSignerCardApplication = async (
  market: string | null,
  signerId: string | null
) => {
  const ACCESS_TOKEN = getCookieValue('additionalSignerAccessToken');

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const response = await axios.get<
    TSharedDTO['getAdditionalSignerCardApplicationDataRes']
  >(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${market}/get-additional-signer-application?product=card&signerId=${signerId}`,
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
    }
  );

  const parseResponseResult = sharedDTOSchemas.getAdditionalSignerCardApplicationDataRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiGetAdditionalSignerCardApplication parseResponseResult'
    );
  }
  return response;
};

export const apiGetLoanOffer = async (
  uuid: string,
  countryCode: CountryCode
) => {
  const ACCESS_TOKEN = await accessToken.get();

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const response = await axios.get<TLoanOfferDTO['getOfferRes']>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${countryCode}/offer/uuid/${uuid}`,
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
    }
  );

  const parseResponseResult = loanOfferDTOSchemas.getOfferRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiGetLoanOffer parseResponseResult'
    );
  }
  return response.data;
};

export const apiPostOffer = async (
  data: Omit<ILoanOfferForm, 'payoutAmount'> & { amount: number },
  market: CountryCode
) => {
  const ACCESS_TOKEN = await accessToken.get();

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }
  const formData = buildPostOfferFormData(data, 'private');

  const response = await axios.post<TLoanOfferDTO['postOfferRes']>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${market}/loans/offer/application/${data.applicationId}/${
      data.clientId
    }/`,
    formData,
    {
      withCredentials: false, // TODO: ONBOARDING: keep or remove?
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
        [HeaderKeys.ContentTypeKey]: 'application/x-www-form-urlencoded',
      },
    }
  );

  const parseResponseResult = loanOfferDTOSchemas.postOfferRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(parseResponseResult.error, 'apiPostOffer parseResponseResult');
  }
  return response.data;
};

export const apiGetKYCQuestionsAndAnswers = async (
  countryCode: CountryCode
) => {
  const ACCESS_TOKEN = await accessToken.get();

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const response = await axios.get<TSharedDTO['getKYCQuestionsAndAnswersRes']>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${countryCode.toLowerCase()}/client-assessment/questions`,
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
      params: {
        typeId: ClientAssessmentQuestionnaireTypeId.KYC_AND_AML,
      },
    }
  );

  const parseResponseResult = sharedDTOSchemas.getKYCQuestionsAndAnswersRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiGetKYCQuestionsAndAnswers parseResponseResult'
    );
  }
  return response.data;
};

export const apiPostKYCAnswers = async (
  countryCode: CountryCode,
  clientId: number,
  kycQuestionnaireAnswers: IKYCQuestionnaireAnswer[]
) => {
  const ACCESS_TOKEN = await accessToken.get();

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const data: TSharedDTO['postKYCAnswersReq'] = {
    questionsAndAnswers: kycQuestionnaireAnswers,
  };

  const parseRequestDataResult = sharedDTOSchemas.postKYCAnswersReq.safeParse(
    data
  );

  if (!parseRequestDataResult.success) {
    logToSentry(
      parseRequestDataResult.error,
      'apiPostKYCAnswers parseRequestDataResult'
    );
  }

  const response = await axios.post<TSharedDTO['postKYCAnswersRes']>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${countryCode.toLowerCase()}/client-assessment/answers/${clientId}`,
    { questionsAndAnswers: kycQuestionnaireAnswers },
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
      params: {
        typeId: ClientAssessmentQuestionnaireTypeId.KYC_AND_AML,
      },
    }
  );

  const parseResponseResult = sharedDTOSchemas.postKYCAnswersRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiPostKYCAnswers parseResponseResult'
    );
  }
  return response.data;
};

export const apiGetLoanApplication = async (
  uuid: string,
  countryCode: CountryCode
) => {
  const ACCESS_TOKEN = await accessToken.get();
  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const response = await axios.get<TLoanApplicationDTO['getApplicationRes']>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${countryCode}/application/${uuid}`,
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
    }
  );

  const parseResponseResult = loanApplicationDTOSchemas.getApplicationRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiGetLoanApplication parseResponseResult'
    );
  }
  return response.data;
};

export const apiGetClientCompanies = async (countryCode: CountryCode) => {
  const ACCESS_TOKEN = await accessToken.get();

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const response = await axios.get<TLoanApplicationDTO['getClientsRes']>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${countryCode}/clients`,
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
    }
  );

  const parseResponseResult = loanApplicationDTOSchemas.getClientsRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiGetClientCompanies parseResponseResult'
    );
  }
  return response.data;
};

export const apiGetGuarantorsAndBoardMembersV2 = async ({
  applicationUuid,
  countryCode,
}: {
  applicationUuid: string;
  countryCode: CountryCode;
}) => {
  const ACCESS_TOKEN = await accessToken.get();
  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const url = `${
    import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
  }/country/${countryCode.toLowerCase()}/guarantors/applicationUuid/${applicationUuid}`;

  const options: AxiosRequestConfig = {
    headers: {
      [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
    },
  };

  const response = await axios.get<
    TLoanApplicationDTO['getGuarantorsAndBoardMembersRes'][]
  >(url, options);

  const parseResponseResult = loanApplicationDTOSchemas.getGuarantorsAndBoardMembersRes
    .array()
    .safeParse(response.data);

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiGetGuarantorsAndBoardMembers parseResponseResult'
    );
  }

  return response;
};

export const apiGetGuarantorsAndBoardMembers = async (
  applicationUuid: string,
  countryCode: CountryCode,
  withRetry: boolean
) => {
  const ACCESS_TOKEN = await accessToken.get();
  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const axiosInstance = withRetry ? axiosWithRetry : axios;

  const options: AxiosRequestConfig = {
    headers: {
      [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
    },
  };
  if (withRetry) {
    options.retry = {
      retryCount: 3,
      retryDelay: 3000,
      retryUntil: (data: any) => Array.isArray(data) && !!data.length,
    };
  }

  const response = await axiosInstance.get<
    TLoanApplicationDTO['getGuarantorsAndBoardMembersRes'][]
  >(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${countryCode.toLowerCase()}/guarantors/applicationUuid/${applicationUuid}`,
    options
  );

  const parseResponseResult = loanApplicationDTOSchemas.getGuarantorsAndBoardMembersRes
    .array()
    .safeParse(response.data);

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiGetGuarantorsAndBoardMembers parseResponseResult'
    );
  }

  return response;
};

export const apiGetBeneficialOwnersFromRoaring = async (
  orgNumber: string,
  countryCode: CountryCode
) => {
  const ACCESS_TOKEN = await accessToken.get();

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const getBeneficialOwnersResponse = await axios.get<
    TLoanOfferDTO['getBeneficialOwnersFromRoaringRes']
  >(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${countryCode.toLowerCase()}/roaring-beneficial-owners/${orgNumber}`,
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
    }
  );

  const parseResponseResult = loanOfferDTOSchemas.getBeneficialOwnersFromRoaringRes.safeParse(
    getBeneficialOwnersResponse.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiGetBeneficialOwnersFromRoaring parseResponseResult'
    );
  }
  return getBeneficialOwnersResponse.data;
};

export const apiGetBeneficialOwners = async (
  countryCode: CountryCode,
  applicationUuid: string
) => {
  const ACCESS_TOKEN = await accessToken.get();

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const getBeneficialOwnersResponse = await axios.get<
    TLoanOfferDTO['getBeneficialOwnersRes']
  >(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${countryCode.toLowerCase()}/beneficial-owners/${applicationUuid}`,
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
    }
  );

  const parseResponseResult = loanOfferDTOSchemas.getBeneficialOwnersRes.safeParse(
    getBeneficialOwnersResponse.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiGetBeneficialOwners parseResponseResult'
    );
  }
  return getBeneficialOwnersResponse.data;
};

export const apiPostIdentify = async (
  countryCode: CountryCode,
  applicationUuid: string
) => {
  const ACCESS_TOKEN = await accessToken.get();

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const response = await axios.post<TSharedDTO['postIdentifyRes']>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${countryCode.toLowerCase()}/identify/${applicationUuid}`,
    null,
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
    }
  );

  const parseResponseResult = sharedDTOSchemas.postIdentifyRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiPostIdentify parseResponseResult'
    );
  }
  return response.data;
};

export const apiPostLoanApplication = async (
  data: TLoanApplicationDTO['postPatchApplicationReq']
) => {
  const config = { headers: {} };
  const ACCESS_TOKEN = await accessToken.get();
  if (ACCESS_TOKEN) {
    config.headers = {
      [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
    };
  }

  const parseRequestDataResult = loanApplicationDTOSchemas.postPatchApplicationReq.safeParse(
    data
  );

  if (!parseRequestDataResult.success) {
    logToSentry(
      parseRequestDataResult.error,
      'apiPostLoanApplication parseRequestDataResult'
    );
  }

  const response = await axios.post<TLoanApplicationDTO['postApplicationRes']>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${data.market.toLowerCase()}/application/`,
    data,
    config
  );

  const parseResponseResult = loanApplicationDTOSchemas.postApplicationRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiPostLoanApplication parseResponseResult'
    );
  }
  return response.data;
};

export const apiGetCardApplication = async (
  uuid: string,
  countryCode: CountryCode
): Promise<CardApplicationDTO> => {
  const ACCESS_TOKEN = await accessToken.get();

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const response = await axios.get<CardApplicationDTO>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${countryCode}/card-application/${uuid}`,
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
    }
  );

  const parseResponseResult = cardApplicationDTOSchema.safeParse(response.data);

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiGetCardApplication parseResponseResult'
    );
  }
  return response.data;
};

export const apiPostCardApplication = async (
  data: CardApplicationDTO
): Promise<CardApplicationPostPatchResponseDTO> => {
  const config: { headers: Record<string, string> } = { headers: {} };
  const ACCESS_TOKEN = await accessToken.get();
  if (ACCESS_TOKEN) {
    config.headers = {
      [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
    };
  }

  const parseRequestDataResult = cardApplicationDTOSchema.safeParse(data);

  if (!parseRequestDataResult.success) {
    logToSentry(
      parseRequestDataResult.error,
      'apiPostCardApplication parseRequestDataResult'
    );
  }

  const response = await axios.post<CardApplicationPostPatchResponseDTO>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${data.market.toLowerCase()}/card-application/`,
    data,
    config
  );

  const parseResponseResult = cardApplicationPostPatchResponseSchema.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiPostCardApplication parseResponseResult'
    );
  }

  return response.data;
};

export const apiPatchCardApplication = async (
  data: CardApplicationDTO
): Promise<CardApplicationPostPatchResponseDTO> => {
  const ACCESS_TOKEN = await accessToken.get();

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const parseRequestDataResult = cardApplicationDTOSchema.safeParse(data);

  if (!parseRequestDataResult.success) {
    logToSentry(
      parseRequestDataResult.error,
      'apiPatchCardApplication parseRequestDataResult'
    );
  }
  const response = await axios.patch<CardApplicationPostPatchResponseDTO>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${data.market.toLowerCase()}/card-application/${
      data.applicationUuid
    }`,
    data,
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
    }
  );

  const parseResponseResult = cardApplicationPostPatchResponseSchema.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiPatchCardApplication parseResponseResult'
    );
  }
  return response.data;
};

export const apiPatchLoanApplication = async (
  data: TLoanApplicationDTO['postPatchApplicationReq'],
  getCompanyTypeCategory: boolean
) => {
  const ACCESS_TOKEN = await accessToken.get();
  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const parseRequestDataResult = loanApplicationDTOSchemas.postPatchApplicationReq.safeParse(
    data
  );

  if (!parseRequestDataResult.success) {
    logToSentry(
      parseRequestDataResult.error,
      'apiPatchLoanApplication parseRequestDataResult'
    );
  }
  const response = await axios.patch<
    TLoanApplicationDTO['patchApplicationRes']
  >(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${data.market.toLowerCase()}/application/${data.applicationUuid}`,
    data,
    {
      params: {
        getCompanyTypeCategory: getCompanyTypeCategory || undefined,
      },
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
    }
  );

  const parseResponseResult = loanApplicationDTOSchemas.patchApplicationRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiPatchLoanApplication parseResponseResult'
    );
  }
  return response.data;
};

export const apiGetAddress = async (
  zipCode: string | undefined,
  houseNumber: string | undefined,
  market: CountryCode
) => {
  const ACCESS_TOKEN = await accessToken.get();
  if (!ACCESS_TOKEN) {
    throw new Error('No ACCESSS_TOKEN found!');
  }
  const response = await axios.get<TSharedDTO['getAddressRes']>(
    `${
      import.meta.env.VITE_API_ONBOARDING_BASE_URL
    }/country/${market}/postcode/${zipCode}/houseNumber/${houseNumber}/address`,
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
    }
  );

  const parseResponseResult = sharedDTOSchemas.getAddressRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(parseResponseResult.error, 'apiGetAddress parseResponseResult');
  }
  return response.data;
};

export const getSignedUrl = async (
  urlList: string[],
  clientId: string,
  market: CountryCode,
  documentType: DocumentType
): Promise<AxiosResponse<IFileInformation[]>> => {
  const ACCESS_TOKEN = await accessToken.get();
  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }
  const getSignedUrlsPayload = {
    fileNames: urlList,
    documentType,
  };

  return axios.post(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${market}/upload/${clientId}`,
    JSON.stringify(getSignedUrlsPayload),
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
        [HeaderKeys.ContentTypeKey]: ContentType.ApplicationJson,
      },
    }
  );
};

export const postFile = (
  url: string,
  data: File | FormData,
  method: 'POST' | 'PUT' = 'POST'
): Promise<AxiosResponse<IFileInformation[]>> =>
  axios.request({
    url,
    method,
    data,
  });

export const apiPostCardOffer = async ({
  data,
  market,
}: {
  data: TCardOfferDTO['postOfferReq'];
  market: CountryCode;
}) => {
  const ACCESS_TOKEN = await accessToken.get();

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const parseRequestDataResult = cardOfferDTOSchemas.postOfferReq.safeParse(
    data
  );

  if (!parseRequestDataResult.success) {
    logToSentry(
      parseRequestDataResult.error,
      'apiPostCardOffer parseRequestDataResult'
    );
  }

  const response = await axios.post<TCardOfferDTO['postOfferRes']>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${market}/card-offer/${data.applicant.signerUuid}`,
    data,
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
    }
  );

  // something went wrong
  if (response?.data?.message) {
    throw new Error(response.data.message);
  }

  const parseResponseResult = cardOfferDTOSchemas.postOfferRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiPostCardOffer parseResponseResult'
    );
  }
  return response.data;
};

// The offer key is the card-offer page's URL segment: an applicationUuid on links sent from
// now on, a clientId on older links and in qredApp. svc-onboarding accepts either.
export const apiGetCardOffer = async (
  offerKey: number | string,
  market: CountryCode
) => {
  const ACCESS_TOKEN = await accessToken.get();

  if (!ACCESS_TOKEN) {
    return Promise.reject(new Error('No ACCESS_TOKEN found!'));
  }

  const response = await axios.get<TCardOfferDTO['getOfferRes']>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${market}/card-offer/${offerKey}`,
    {
      headers: {
        [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${ACCESS_TOKEN}`,
      },
    }
  );

  const parseResponseResult = cardOfferDTOSchemas.getOfferRes.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiGetCardOffer parseResponseResult'
    );
  }
  return response.data;
};

export const apiGetCompanyInfoBySearch = async (
  searchInput: string,
  market: CountryCode,
  address?: string
) => {
  const response = await axios.get<TCompanySearchResults>(
    `${
      import.meta.env.VITE_API_SVC_ONBOARDING_BASE_URL
    }/country/${market}/company-search/${encodeURIComponent(searchInput)}`,
    { params: { address } }
  );

  const parseResponseResult = getCompanySearchResDTOSchema.safeParse(
    response.data
  );

  if (!parseResponseResult.success) {
    logToSentry(
      parseResponseResult.error,
      'apiGetCompanyInfoBySearch parseResponseResult'
    );
  }

  return response.data;
};
